/**
 * Auszahlungen an Helfer: wer zahlt, wie — und was der Steuerberater davon erfährt.
 *
 * Zwei Achsen wie am Beleg: `zahlungsweg` (GbR-Konto oder ein Inhaber) und
 * `zahlungsart` (Überweisung oder bar). Die GbR hat keine Barkasse (Benjamin,
 * 28.09.2026) — „bar" heißt immer: ein Inhaber aus eigener Tasche. Deshalb
 * gibt es hier drei Möglichkeiten statt vier, und bar verlangt den Inhaber.
 *
 * Eine private Zahlung ist eine Einlage des Inhabers. Die bucht der Server,
 * und er schickt einen Zahlungsnachweis an DATEV — sonst findet der
 * Steuerberater zur Rechnung keine Bankzeile und führt sie als offen.
 * Der Versand ist seit 07.10.2026 abwählbar (Haken im Dialog).
 */
import { useState } from "react";
import { fehlerText } from "@vimaana/ui";
import { useCreatePayout, useNachweisSenden, usePayBeleg, useUpdatePayout } from "../../hooks/useTeam";
import type { HelferBeleg } from "../../lib/types";
import type { TeamOwner, TeamPayout, Zahlungsart } from "../../lib/team";
import { shortDate } from "../../lib/team";
import { Dialog, DialogActions, Field } from "./TeamDialogs";

export type Zahlweg = "gbr_konto" | "bar" | "privat";

const WEGE: { wert: Zahlweg; titel: string; unter: string }[] = [
  { wert: "gbr_konto", titel: "GbR-Konto", unter: "Überweisung der Firma" },
  { wert: "bar", titel: "Bar", unter: "aus eigener Tasche" },
  { wert: "privat", titel: "Privat überwiesen", unter: "vom eigenen Konto" },
];

/** Der Zahlweg einer gebuchten Auszahlung — der Altwert zahlungsweg="bar" zählt als bar. */
export function zahlwegVon(payout?: TeamPayout): Zahlweg {
  if (!payout) return "gbr_konto";
  if (payout.zahlungsweg === "bar" || payout.zahlungsart === "bar") return "bar";
  return payout.zahlungsweg === "inhaber" ? "privat" : "gbr_konto";
}

/** Kurzform für die Zeile im Lohnkonto: „bar · Benjamin Hermsdorf". */
export function zahlwegLabel(payout: TeamPayout): string {
  const weg = zahlwegVon(payout);
  const wer = payout.inhaber_name || "Inhaber";
  if (weg === "bar") return `bar · ${wer}`;
  if (weg === "privat") return `privat überwiesen · ${wer}`;
  return "GbR-Konto";
}

function zahlwegBody(weg: Zahlweg, ownerId: string): {
  zahlungsweg: "gbr_konto" | "inhaber"; zahlungsart: Zahlungsart; inhaber_id: string;
} {
  if (weg === "gbr_konto") return { zahlungsweg: "gbr_konto", zahlungsart: "ueberweisung", inhaber_id: "" };
  return { zahlungsweg: "inhaber", zahlungsart: weg === "bar" ? "bar" : "ueberweisung", inhaber_id: ownerId };
}

/** Auswahl Zahlweg + Inhaber. Drei Knöpfe statt Auswahlliste — die Wahl muss man sehen. */
function ZahlwegWahl({ weg, setWeg, ownerId, setOwnerId, owners }: {
  weg: Zahlweg;
  setWeg: (weg: Zahlweg) => void;
  ownerId: string;
  setOwnerId: (id: string) => void;
  owners: TeamOwner[];
}) {
  return (
    <>
      <div className="team-field team-field-full">
        <span>Wer hat gezahlt, und wie?</span>
        <div className="team-zahlweg" role="group" aria-label="Zahlungsweg">
          {WEGE.map((w) => (
            <button type="button" key={w.wert} aria-pressed={weg === w.wert} onClick={() => setWeg(w.wert)}>
              {w.titel}<small>{w.unter}</small>
            </button>
          ))}
        </div>
      </div>
      {weg !== "gbr_konto" && (
        <Field label={weg === "bar" ? "Bar bezahlt von" : "Überwiesen von"}>
          <select value={ownerId} onChange={(event) => setOwnerId(event.target.value)}>
            <option value="">Bitte wählen</option>
            {owners.filter((owner) => owner.active).map((owner) => <option value={owner.id} key={owner.id}>{owner.name}</option>)}
          </select>
        </Field>
      )}
    </>
  );
}

/**
 * Hinweis zur Einlage plus die Wahl, ob der Eigenbeleg gleich an DATEV geht
 * (Benjamin, 07.10.2026: „muss als Option per Checkbox zur Verfügung stehen").
 * Ohne Haken bucht Kontor nur die Einlage; die Auszahlung zeigt dann
 * „Zahlungsnachweis fehlt" und den Knopf zum Nachsenden.
 */
function PrivatHinweis({ weg, nachweis, setNachweis }: {
  weg: Zahlweg;
  nachweis: boolean;
  setNachweis: (an: boolean) => void;
}) {
  if (weg === "gbr_konto") return null;
  return (
    <>
      <p className="team-hint">
        Das ist eine <strong>Einlage</strong> des Inhabers: Kontor bucht sie auf sein Gesellschafterkonto.
      </p>
      <label className="team-check team-check-block">
        <input type="checkbox" checked={nachweis} onChange={(event) => setNachweis(event.target.checked)} />
        <span>
          Eigenbeleg erstellen und an DATEV senden
          <small>
            {nachweis
              ? `Der Steuerberater bekommt einen Zahlungsnachweis.${weg === "bar" ? " Darauf ist Platz für die Unterschrift des Empfängers." : ""}`
              : "Nur die Einlage wird gebucht. Den Nachweis kannst du später an der Auszahlung senden."}
          </small>
        </span>
      </label>
    </>
  );
}

/**
 * Eine Helferrechnung bezahlt buchen.
 *
 * Der Server legt daraus die Auszahlung aufs Lohnkonto an — der offene Saldo
 * sinkt also über dieselbe Formel wie bei jeder anderen Auszahlung, statt über
 * eine zweite Zahlenwelt nur für Rechnungen. Rechnungsbetrag und verdiente
 * Stunden dürfen auseinanderliegen; deshalb ist der Betrag änderbar.
 */
export function BelegBezahltDialog({ memberId, beleg, owners, onClose }: {
  memberId: string;
  beleg: HelferBeleg;
  owners: TeamOwner[];
  onClose: () => void;
}) {
  const buchen = usePayBeleg(memberId);
  const [amount, setAmount] = useState(String(beleg.bruttobetrag || "").replace(".", ","));
  const [datum, setDatum] = useState(new Date().toISOString().slice(0, 10));
  const [weg, setWeg] = useState<Zahlweg>("gbr_konto");
  const [ownerId, setOwnerId] = useState("");
  const [nachweis, setNachweis] = useState(true);
  const [note, setNote] = useState(
    `Rechnung ${beleg.lieferant || "Helfer"} vom ${shortDate(beleg.datum)}`,
  );
  const betrag = Number(amount.replace(",", "."));
  const valid = betrag > 0 && Boolean(datum) && (weg === "gbr_konto" || Boolean(ownerId));

  const save = () => buchen.mutate({
    belegId: beleg.id,
    body: { betrag, zahlungsdatum: datum, vermerk: note.trim(), ...zahlwegBody(weg, ownerId), nachweis },
  }, { onSuccess: onClose });

  return (
    <Dialog title="Rechnung bezahlt buchen" onClose={onClose}>
      <div className="team-form-grid">
        <ZahlwegWahl weg={weg} setWeg={setWeg} ownerId={ownerId} setOwnerId={setOwnerId} owners={owners} />
        <Field label="Betrag (€)"><input autoFocus inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} /></Field>
        <Field label="Zahlungsdatum"><input type="date" value={datum} onChange={(event) => setDatum(event.target.value)} /></Field>
        <Field label="Vermerk" full><textarea rows={2} value={note} onChange={(event) => setNote(event.target.value)} /></Field>
      </div>
      <p className="team-hint">Daraus entsteht eine Auszahlung auf dem Lohnkonto — der offene Saldo sinkt entsprechend.</p>
      <PrivatHinweis weg={weg} nachweis={nachweis} setNachweis={setNachweis} />
      {buchen.isError && <p className="team-error">{fehlerText(buchen.error, "Die Rechnung konnte nicht gebucht werden.")}</p>}
      <DialogActions onClose={onClose} onSave={save} disabled={!valid || buchen.isPending} busy={buchen.isPending} />
    </Dialog>
  );
}

/**
 * Auszahlung anlegen oder ändern.
 *
 * Beim Ändern zieht der Server die Inhaber-Einlage mit nach: wer von privat
 * auf „GbR-Konto" wechselt, dessen Einlage verschwindet, und umgekehrt
 * entsteht sie. Ein schon verschickter Zahlungsnachweis geht nach einer
 * Änderung als berichtigte Fassung erneut an DATEV.
 */
export function PayoutDialog({ memberId, owners, payout, onClose }: {
  memberId: string;
  owners: TeamOwner[];
  payout?: TeamPayout;
  onClose: () => void;
}) {
  const create = useCreatePayout(memberId);
  const update = useUpdatePayout(memberId);
  const aendern = Boolean(payout);
  const [amount, setAmount] = useState(payout ? String(payout.betrag).replace(".", ",") : "");
  const [date, setDate] = useState(payout?.zahlungsdatum || new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState(payout?.vermerk || "");
  const [weg, setWeg] = useState<Zahlweg>(zahlwegVon(payout));
  const [ownerId, setOwnerId] = useState(payout?.inhaber_id || "");
  const [mitNachweis, setMitNachweis] = useState(true);
  const betrag = Number(amount.replace(",", "."));
  const valid = betrag > 0 && Boolean(date) && (weg === "gbr_konto" || Boolean(ownerId));
  const busy = create.isPending || update.isPending;
  const warPrivat = aendern && zahlwegVon(payout) !== "gbr_konto";
  const nachweis = payout?.nachweis_nummer || "";

  const save = () => {
    const daten = { betrag, zahlungsdatum: date, vermerk: note.trim(), ...zahlwegBody(weg, ownerId) };
    if (payout) update.mutate({ payoutId: payout.id, patch: daten }, { onSuccess: onClose });
    else create.mutate({ ...daten, nachweis: mitNachweis }, { onSuccess: onClose });
  };
  const fehler = create.error || update.error;

  return (
    <Dialog title={aendern ? "Auszahlung ändern" : "Auszahlung erfassen"} onClose={onClose}>
      <div className="team-form-grid">
        <ZahlwegWahl weg={weg} setWeg={setWeg} ownerId={ownerId} setOwnerId={setOwnerId} owners={owners} />
        <Field label="Betrag (€)"><input autoFocus inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} /></Field>
        <Field label="Zahlungsdatum"><input type="date" value={date} onChange={(event) => setDate(event.target.value)} /></Field>
        <Field label="Vermerk" full><textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} /></Field>
      </div>
      {!aendern && <PrivatHinweis weg={weg} nachweis={mitNachweis} setNachweis={setMitNachweis} />}
      {aendern && !warPrivat && weg !== "gbr_konto" && (
        <p className="team-hint">
          Die Einlage wird gebucht. Den Zahlungsnachweis schickst du danach an der Auszahlung mit
          „Zahlungsnachweis senden" ab — so entscheidest du bei alten Zahlungen selbst, ob der
          Steuerberater ihn noch bekommen soll.
        </p>
      )}
      {warPrivat && weg === "gbr_konto" && (
        <p className="team-hint">
          Die private Einlage von {payout?.inhaber_name || "dem Inhaber"} wird dabei zurückgenommen.
          {nachweis && ` Der Zahlungsnachweis ${nachweis} gilt dann nicht mehr — bitte dem Steuerberater Bescheid geben.`}
        </p>
      )}
      {warPrivat && weg !== "gbr_konto" && payout?.nachweis_status === "versendet" && (
        <p className="team-hint">Ändert sich Betrag, Datum oder Zahler, geht {nachweis} als berichtigte Fassung erneut an DATEV.</p>
      )}
      {fehler && <p className="team-error">{fehlerText(fehler, "Die Auszahlung konnte nicht gespeichert werden.")}</p>}
      <DialogActions onClose={onClose} onSave={save} disabled={!valid || busy} busy={busy} />
    </Dialog>
  );
}

const NACHWEIS_PILLE: Record<string, { text: (nr: string) => string; ton: string; titel: string }> = {
  versendet: { text: (nr) => `${nr} · DATEV ✓`, ton: "success", titel: "Zahlungsnachweis an DATEV übermittelt" },
  nicht_versendet: { text: () => "Nachweis nicht übermittelt", ton: "warning", titel: "Der Versand an DATEV ist fehlgeschlagen" },
  veraltet: { text: () => "Nachweis veraltet", ton: "warning", titel: "Betrag, Datum oder Zahler wurden geändert" },
  fehlt: { text: () => "Zahlungsnachweis fehlt", ton: "warning", titel: "Der Steuerberater weiß noch nicht, dass privat gezahlt wurde" },
  ungueltig: { text: (nr) => `${nr} gilt nicht mehr`, ton: "danger", titel: "Die Zahlung läuft jetzt übers GbR-Konto — bitte dem Steuerberater Bescheid geben" },
};

/**
 * Zustand des Zahlungsnachweises an einer Auszahlung — und der eine Knopf, der ihn in Ordnung bringt.
 *
 * Der Knopf heißt je nach Lage anders, tut aber immer dasselbe: erzeugen, was
 * fehlt oder veraltet ist, und schicken, was noch nicht raus ist.
 */
export function NachweisAktion({ memberId, payout, onAnsehen }: {
  memberId: string;
  payout: TeamPayout;
  onAnsehen: () => void;
}) {
  const senden = useNachweisSenden(memberId);
  const status = payout.nachweis_status;
  if (!status) return null;
  const pille = NACHWEIS_PILLE[status];
  const knopf = status === "fehlt" ? "Zahlungsnachweis senden"
    : status === "nicht_versendet" ? "Erneut senden"
    : status === "veraltet" ? "Berichtigen & senden" : "";
  const sendefehler = senden.data?.fehler || "";
  const bestaetigen = () => {
    if (status === "fehlt" && !window.confirm(
      `Zahlungsnachweis für ${shortDate(payout.zahlungsdatum)} erstellen und an DATEV schicken?`,
    )) return;
    senden.mutate(payout.id);
  };
  return (
    <>
      {pille && (
        <span className={`team-pill ${pille.ton}`} title={payout.nachweis_versendet_am
          ? `${pille.titel} am ${shortDate(payout.nachweis_versendet_am)}` : pille.titel}>
          {pille.text(payout.nachweis_nummer)}
        </span>
      )}
      {payout.nachweis_nummer && (
        <button type="button" className="team-text-action" onClick={onAnsehen}>Nachweis ansehen</button>
      )}
      {knopf && (
        <button type="button" className="team-text-action" disabled={senden.isPending} onClick={bestaetigen}>
          {senden.isPending ? "Sendet …" : knopf}
        </button>
      )}
      {senden.isError && <span className="team-error">{fehlerText(senden.error, "Der Nachweis konnte nicht erstellt werden.")}</span>}
      {sendefehler && <span className="team-error">DATEV-Versand fehlgeschlagen: {sendefehler}</span>}
    </>
  );
}
